// API Base URL from environment variable
const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api/v1';

// Tenant slug localStorage key
const TENANT_SLUG_KEY = 'tenant_slug';

// Get tenant slug from subdomain, localStorage, env var, or fallback
export function getTenantSlug(): string {
  // 1. Subdomain extraction — most authoritative (e.g., zuck-group.tabillo.com)
  const host = window.location.hostname;
  if (host.endsWith('.tabillo.com')) {
    const subdomain = host.slice(0, host.indexOf('.'));
    const reserved = [
      // Platform infrastructure
      'admin', 'api', 'www', 'mail', 'ftp', 'smtp',
      'help', 'support', 'status', 'health', 'ping',
      'app', 'apps', 'signup', 'login', 'dashboard',
      'staging', 'preview', 'test', 'sandbox', 'beta',
      'docs', 'blog', 'cdn', 'static', 'assets', 'media',
      // Internal Tabillo instances
      'demo', 'internal', 'team', 'marketing', 'finance',
      'customersuccess', 'customer-success', 'intranet', 'extranet',
      'partners', 'security', 'compliance',
    ];
    if (!reserved.includes(subdomain)) {
      return subdomain;
    }
  }

  // 2. localStorage (for non-subdomain access: multi-workspace picker, dev)
  const storedSlug = localStorage.getItem(TENANT_SLUG_KEY);
  if (storedSlug) return storedSlug;

  // 3. Env var (local development override)
  const envSlug = import.meta.env.VITE_TENANT_SLUG;
  if (envSlug) return envSlug;

  // 4. No slug found — return empty; callers should redirect to login
  return '';
}

// Returns true when running on master.tabillo.com
export function isMasterHost(): boolean {
  return window.location.hostname === 'master.tabillo.com';
}

// Returns true when running on apps.tabillo.com (public login/signup hub)
export function isAppsHost(): boolean {
  return window.location.hostname === 'apps.tabillo.com';
}

/** @deprecated Use isMasterHost() instead */
export function isPlatformAdmin(): boolean {
  return isMasterHost();
}

// Set tenant slug in localStorage
export function setTenantSlug(slug: string): void {
  localStorage.setItem(TENANT_SLUG_KEY, slug);
}

// Clear tenant slug from localStorage
export function clearTenantSlug(): void {
  localStorage.removeItem(TENANT_SLUG_KEY);
}

// Custom API Error with full details
export class ApiError extends Error {
  public readonly details: string;

  constructor(
    message: string,
    public readonly method: string,
    public readonly url: string,
    public readonly status: number,
    public readonly statusText: string,
    public readonly responseBody: string,
    public readonly requestBody?: string
  ) {
    super(message);
    this.name = 'ApiError';
    this.details = [
      `Error: ${message}`,
      ``,
      `Request:`,
      `  ${method} ${url}`,
      requestBody ? `  Body: ${requestBody}` : null,
      ``,
      `Response:`,
      `  Status: ${status} ${statusText}`,
      `  Body: ${responseBody || '(empty)'}`,
      ``,
      `Time: ${new Date().toISOString()}`,
    ].filter(Boolean).join('\n');
  }
}

// Token storage key
const AUTH_TOKEN_KEY = 'auth_token';

// Get stored auth token
export function getAuthToken(): string | null {
  return localStorage.getItem(AUTH_TOKEN_KEY);
}

// Set auth token
export function setAuthToken(token: string): void {
  localStorage.setItem(AUTH_TOKEN_KEY, token);
}

// Clear auth token
export function clearAuthToken(): void {
  localStorage.removeItem(AUTH_TOKEN_KEY);
}

// Global "session expired" handler — invoked when an authenticated request gets a 401
// (token expired/invalidated mid-session). Registered by AuthContext so the app can surface
// a "please sign in again" prompt instead of a cryptic inline error.
let onUnauthorized: (() => void) | null = null;
export function setUnauthorizedHandler(handler: (() => void) | null): void {
  onUnauthorized = handler;
}

// Endpoints where a 401 is an expected, normal outcome (bad login / initial "am I logged in?"
// check / logout) — these must NOT trigger the session-expired UX.
const AUTH_ENDPOINTS_EXEMPT_FROM_401 = ['/auth/request-otp', '/auth/verify-otp', '/auth/me', '/auth/logout'];

// Generic API request helper
async function apiRequest<T>(
  endpoint: string,
  options?: RequestInit
): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`;
  const method = options?.method || 'GET';
  const requestBody = options?.body as string | undefined;

  // Get auth token from localStorage
  const token = getAuthToken();

  let response: Response;
  try {
    response = await fetch(url, {
      headers: {
        'Content-Type': 'application/json',
        'x-tenant-slug': getTenantSlug(),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...options?.headers,
      },
      credentials: 'include', // Still include cookies as fallback
      ...options,
    });
  } catch (networkError) {
    throw new ApiError(
      `Network error: ${networkError}`,
      method,
      url,
      0,
      'Network Error',
      String(networkError),
      requestBody
    );
  }

  if (!response.ok) {
    const responseBody = await response.text();
    let errorMessage = `HTTP ${response.status}`;
    try {
      const errorJson = JSON.parse(responseBody);
      const baseMsg = errorJson.error || errorMessage;
      const detail = errorJson.message;
      errorMessage = detail ? `${baseMsg}: ${detail}` : baseMsg;
    } catch {
      if (responseBody) errorMessage = responseBody;
    }
    // Session expired/invalidated mid-session → notify the app (modal + redirect).
    // Skip auth endpoints where a 401 is a normal outcome.
    if (
      response.status === 401 &&
      onUnauthorized &&
      !AUTH_ENDPOINTS_EXEMPT_FROM_401.some((p) => endpoint.startsWith(p))
    ) {
      onUnauthorized();
    }
    throw new ApiError(
      errorMessage,
      method,
      url,
      response.status,
      response.statusText,
      responseBody,
      requestBody
    );
  }

  if (response.status === 204) {
    return null as T;
  }

  return response.json();
}

// Tag API
export const ownerApi = {
  getAll: () => apiRequest<any[]>('/owners'),
  getById: (id: string) => apiRequest<any>(`/owners/${id}`),
  create: (data: any) => apiRequest<any>('/owners', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  update: (id: string, data: any) => apiRequest<any>(`/owners/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  delete: (id: string) => apiRequest<void>(`/owners/${id}`, {
    method: 'DELETE',
  }),
};

export const tagApi = {
  getAll: () => apiRequest<any[]>('/tags'),
  getById: (id: string) => apiRequest<any>(`/tags/${id}`),
  create: (data: any) => apiRequest<any>('/tags', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  update: (id: string, data: any) => apiRequest<any>(`/tags/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  delete: (id: string) => apiRequest<void>(`/tags/${id}`, {
    method: 'DELETE',
  }),
};

// Classification API
export const classificationApi = {
  getAll: () => apiRequest<any[]>('/classifications'),
  getById: (id: string) => apiRequest<any>(`/classifications/${id}`),
  create: (data: any) => apiRequest<any>('/classifications', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  update: (id: string, data: any) => apiRequest<any>(`/classifications/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  delete: (id: string) => apiRequest<void>(`/classifications/${id}`, {
    method: 'DELETE',
  }),
};

// Status API
export const statusApi = {
  getAll: () => apiRequest<any[]>('/status'),
  getById: (id: string) => apiRequest<any>(`/status/${id}`),
  create: (data: any) => apiRequest<any>('/status', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  update: (id: string, data: any) => apiRequest<any>(`/status/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  delete: (id: string) => apiRequest<void>(`/status/${id}`, {
    method: 'DELETE',
  }),
};

// Product API
export const productApi = {
  getAll: () => apiRequest<any[]>('/products'),
  getById: (id: string) => apiRequest<any>(`/products/${id}`),
  create: (data: any) => apiRequest<any>('/products', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  update: (id: string, data: any) => apiRequest<any>(`/products/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  delete: (id: string) => apiRequest<void>(`/products/${id}`, {
    method: 'DELETE',
  }),
};

// Sub-product API
export const subProductApi = {
  getAll: () => apiRequest<any[]>('/sub-products'),
  getById: (id: string) => apiRequest<any>(`/sub-products/${id}`),
  getByProduct: (productId: string) =>
    apiRequest<any[]>(`/sub-products/by-product/${productId}`),
  create: (data: any) => apiRequest<any>('/sub-products', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  update: (id: string, data: any) => apiRequest<any>(`/sub-products/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  delete: (id: string) => apiRequest<void>(`/sub-products/${id}`, {
    method: 'DELETE',
  }),
};

// Initiative API
export const initiativeApi = {
  getAll: () => apiRequest<any[]>('/initiatives'),
  getById: (id: string) => apiRequest<any>(`/initiatives/${id}`),
  create: (data: any) => apiRequest<any>('/initiatives', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  update: (id: string, data: any) => apiRequest<any>(`/initiatives/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  delete: (id: string) => apiRequest<void>(`/initiatives/${id}`, {
    method: 'DELETE',
  }),
  getAuditLog: (id: string) => apiRequest<any[]>(`/initiatives/${id}/audit-log`),
};

// Comment API
export interface Comment {
  id: string;
  initiativeId: string;
  userId: string;
  content: string;
  parentId: string | null;
  createdAt: string;
  updatedAt: string;
  isPinned?: boolean;
  user: {
    id: string;
    name: string;
    email: string;
  };
  replies?: Comment[];
}

// Recent comment with initiative info
export interface RecentComment {
  id: string;
  content: string;
  createdAt: string;
  parentId: string | null;
  user: {
    id: string;
    name: string;
    email: string;
  };
  parent?: {
    id: string;
    user: {
      name: string;
    };
  } | null;
  initiative: {
    id: string;
    title: string;
    subProduct: {
      id: string;
      name: string;
      product: {
        id: string;
        name: string;
      };
    };
  };
}

export const commentApi = {
  getByInitiative: (initiativeId: string) =>
    apiRequest<Comment[]>(`/initiatives/${initiativeId}/comments`),
  getCount: (initiativeId: string) =>
    apiRequest<{ count: number }>(`/initiatives/${initiativeId}/comments/count`),
  getRecent: (limit: number = 15, offset: number = 0) =>
    apiRequest<{ data: RecentComment[]; total: number; hasMore: boolean }>(
      `/comments/recent?limit=${limit}&offset=${offset}`
    ),
  create: (initiativeId: string, content: string, parentId?: string) =>
    apiRequest<Comment>(`/initiatives/${initiativeId}/comments`, {
      method: 'POST',
      body: JSON.stringify({ content, parentId }),
    }),
  update: (commentId: string, content: string) =>
    apiRequest<Comment>(`/comments/${commentId}`, {
      method: 'PUT',
      body: JSON.stringify({ content }),
    }),
  delete: (commentId: string) =>
    apiRequest<void>(`/comments/${commentId}`, {
      method: 'DELETE',
    }),
  pin: (commentId: string) =>
    apiRequest<Comment>(`/comments/${commentId}/pin`, { method: 'PATCH' }),
};

// Resource API
export interface Resource {
  id: string;
  initiativeId: string;
  title: string;
  url: string;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
}

export const resourceApi = {
  list: (initiativeId: string) =>
    apiRequest<Resource[]>(`/initiatives/${initiativeId}/resources`),
  create: (initiativeId: string, data: { title: string; url: string }) =>
    apiRequest<Resource>(`/initiatives/${initiativeId}/resources`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  update: (id: string, data: { title: string; url: string }) =>
    apiRequest<Resource>(`/resources/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  delete: (id: string) =>
    apiRequest<void>(`/resources/${id}`, { method: 'DELETE' }),
};

// Dependency API
export const dependencyApi = {
  getCount: (initiativeId: string) =>
    apiRequest<{ count: number }>(`/initiatives/${initiativeId}/dependencies/count`),
  add: (initiativeId: string, dependsOnInitiativeId: string) =>
    apiRequest<any>(`/initiatives/${initiativeId}/dependencies`, {
      method: 'POST',
      body: JSON.stringify({ dependsOnInitiativeId }),
    }),
  remove: (initiativeId: string, dependencyId: string) =>
    apiRequest<void>(`/initiatives/${initiativeId}/dependencies/${dependencyId}`, {
      method: 'DELETE',
    }),
};

// Scoring API
export interface ScoreLogRow {
  id: string;
  title: string;
  startQuarter: string | null;
  endQuarter?: string | null;
  userId: string | null;
  statusId: string;
  subProduct: {
    name: string;
    product: { name: string };
  };
  status: {
    id: string;
    name: string;
    color: string;
  };
  classification: {
    name: string;
    color: string;
  } | null;
  user: { name: string } | null;
  impact: number | null;
  effort: number | null;
  voteCount: number;
  hasVoted: boolean;
}

export interface VoteState {
  count: number;
  hasVoted: boolean;
}

export const scoringApi = {
  get: (initiativeId: string) =>
    apiRequest<any>(`/initiatives/${initiativeId}/scoring`),
  update: (initiativeId: string, data: { impact: number | null; effort: number | null }) =>
    apiRequest<any>(`/initiatives/${initiativeId}/scoring`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  getAll: () =>
    apiRequest<ScoreLogRow[]>('/scoring/all'),
  getVote: (initiativeId: string) =>
    apiRequest<VoteState>(`/initiatives/${initiativeId}/vote`),
  vote: (initiativeId: string) =>
    apiRequest<VoteState>(`/initiatives/${initiativeId}/vote`, {
      method: 'POST',
    }),
};

// Batch Update API (Admin only)
export interface BatchStartResult {
  batchId: string;
  total: number;
  chunkSize: number;
  fieldLabel: string;
  newValueDisplay: string | null;
  skipped: { id: string; title: string; reason: string }[];
}

export interface BatchChunkResult {
  applied: number;
  total: number;
  errors: { id: string; error: string }[];
}

export const batchApi = {
  start: (data: { field: string; value: unknown; tagMode?: string; initiativeIds: string[] }) =>
    apiRequest<BatchStartResult>('/initiatives/batch/start', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  applyChunk: (batchId: string, ids: string[]) =>
    apiRequest<BatchChunkResult>(`/initiatives/batch/${batchId}/apply-chunk`, {
      method: 'POST',
      body: JSON.stringify({ ids }),
    }),
  rollback: (batchId: string) =>
    apiRequest<{ rolledBack: number }>(`/initiatives/batch/${batchId}/rollback`, {
      method: 'POST',
    }),
  list: () => apiRequest<any[]>('/initiatives/batch'),
  get: (batchId: string) => apiRequest<any>(`/initiatives/batch/${batchId}`),
};

// Fiscal Year API
export const fiscalYearApi = {
  getAll: () => apiRequest<any[]>('/fiscal-years'),
  getById: (id: string) => apiRequest<any>(`/fiscal-years/${id}`),
  create: (data: any) => apiRequest<any>('/fiscal-years', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  update: (id: string, data: any) => apiRequest<any>(`/fiscal-years/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  delete: (id: string) => apiRequest<void>(`/fiscal-years/${id}`, {
    method: 'DELETE',
  }),
};

// Timeframe API (new endpoint, same shape as fiscalYearApi)
export const timeframeApi = {
  getAll: () => apiRequest<any[]>('/timeframes'),
  getById: (id: string) => apiRequest<any>(`/timeframes/${id}`),
  create: (data: { prefix: string; template: 'quarterly' | 'monthly' | 'custom'; customPeriods?: Array<{ label: string }> }) =>
    apiRequest<any>('/timeframes', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  update: (id: string, data: any) => apiRequest<any>(`/timeframes/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  delete: (id: string) => apiRequest<void>(`/timeframes/${id}`, {
    method: 'DELETE',
  }),
};

// Field Config API
export interface FieldConfig {
  id: string;
  fieldKey: string;
  label: string;
  isVisible: boolean;
  isRequired: boolean;
  isLocked: boolean;
  displayOrder: number;
  createdAt: string;
  updatedAt: string;
}

export const fieldConfigApi = {
  getAll: () => apiRequest<FieldConfig[]>('/field-config'),
  update: (fields: Array<{ fieldKey: string; label?: string; isVisible?: boolean; isRequired?: boolean }>) =>
    apiRequest<FieldConfig[]>('/field-config', {
      method: 'PUT',
      body: JSON.stringify({ fields }),
    }),
};

// Labs API
export interface LabsFeature {
  featureKey: string;
  moduleKey: string;
  parentFeatureKey: string | null;
  label: string;
  description?: string;
  isEnabled: boolean;
  displayOrder: number;
  updatedAt: string;
}

export const labsApi = {
  getAll: () => apiRequest<LabsFeature[]>('/labs'),
  update: (features: LabsFeature[]) =>
    apiRequest<LabsFeature[]>('/labs', {
      method: 'PUT',
      body: JSON.stringify({ features }),
    }),
};

// Notification API
export interface Notification {
  id: string;
  userId: string;
  type: string;
  message: string;
  commentId: string | null;
  initiativeId: string | null;
  isRead: boolean;
  createdAt: string;
  initiative?: { id: string; title: string } | null;
  actorName?: string;
  initiativeTitle?: string;
  commentPreview?: string;
}

export const notificationApi = {
  list: (limit = 20, offset = 0, type?: string) =>
    apiRequest<{ notifications: Notification[]; total: number; unread: number; hasMore: boolean }>(
      `/notifications?limit=${limit}&offset=${offset}${type ? `&type=${encodeURIComponent(type)}` : ''}`
    ),
  count: () => apiRequest<{ unread: number }>('/notifications/count'),
  readAll: () =>
    apiRequest<{ success: boolean }>('/notifications/read-all', {
      method: 'PATCH',
    }),
  readOne: (id: string) =>
    apiRequest<{ success: boolean }>(`/notifications/${id}/read`, {
      method: 'PATCH',
    }),
  delete: (id: string) =>
    apiRequest<void>(`/notifications/${id}`, { method: 'DELETE' }),
  updatePreferences: (prefs: { emailNotificationsEnabled: boolean }) =>
    apiRequest<{ success: boolean }>('/notifications/preferences', {
      method: 'PUT',
      body: JSON.stringify(prefs),
    }),
};

// Saved Views API
export interface SavedFilterState {
  searchQuery: string;
  quarters: string[];
  products: string[];
  subProducts: string[];
  statuses: string[];
  owners: string[];
  classifications: string[];
  tags: string[];
  hasDependencies: boolean;
  isStarred?: boolean;
  impactValues?: number[];
  effortValues?: number[];
  minVotes?: number;
  // Kanban board column layout (label/color/order/hidden per status). Stored alongside the raw
  // filter fields on the same server-side blob — see KanbanPage.tsx for read/write logic.
  kanbanColumns?: {
    id: string;
    statusId: string | null;
    label: string;
    color: string;
    hidden: boolean;
    order: number;
  }[];
}

export const savedViewApi = {
  get: (viewType: 'timeline' | 'list' | 'kanban' | 'calendar' | 'scoring' | 'audit' | 'comments' | 'dependency') =>
    apiRequest<{ filterState: SavedFilterState | null }>(`/saved-views/${viewType}`),
  save: (viewType: 'timeline' | 'list' | 'kanban' | 'calendar' | 'scoring' | 'audit' | 'comments' | 'dependency', filterState: SavedFilterState) =>
    apiRequest<{ success: boolean }>(`/saved-views/${viewType}`, {
      method: 'PUT',
      body: JSON.stringify({ filterState }),
    }),
  clear: (viewType: 'timeline' | 'list' | 'kanban' | 'calendar' | 'scoring' | 'audit' | 'comments' | 'dependency') =>
    apiRequest<void>(`/saved-views/${viewType}`, { method: 'DELETE' }),
};

// ============================================================================
// Named, shareable Saved Views
// ============================================================================

export type ViewShareType = 'ONLY_ME' | 'EVERYONE' | 'GROUP' | 'TEAM' | 'USER';

export interface SavedView {
  id: string;
  title: string;
  viewType: 'timeline' | 'list' | 'kanban' | 'calendar';
  filterState: SavedFilterState;
  ownerId: string;
  owner: { id: string; name: string };
  createdAt: string;
  updatedAt: string;
  shareType: ViewShareType;
  targets: { id: string; name: string }[];
  canManage: boolean;
  isFavorite: boolean;
}

export interface SaveViewPayload {
  title: string;
  viewType: 'timeline' | 'list' | 'kanban' | 'calendar';
  filterState: SavedFilterState;
  shareType: ViewShareType;
  targetIds: string[];
}

export const viewApi = {
  getAll: () => apiRequest<SavedView[]>('/views'),
  create: (data: SaveViewPayload) =>
    apiRequest<SavedView>('/views', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: Partial<SaveViewPayload>) =>
    apiRequest<SavedView>(`/views/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id: string) => apiRequest<void>(`/views/${id}`, { method: 'DELETE' }),
  favorite: (id: string) =>
    apiRequest<{ viewType: 'timeline' | 'list' | 'kanban' | 'calendar'; favoriteViewId: string | null }>(`/views/${id}/favorite`, { method: 'POST' }),
  unfavorite: (id: string) =>
    apiRequest<{ viewType: 'timeline' | 'list' | 'kanban' | 'calendar'; favoriteViewId: string | null }>(`/views/${id}/favorite`, { method: 'DELETE' }),
};

// Stats API
export interface StatsResponse {
  total: number;
  byStatus: {
    id: string;
    name: string;
    color: string;
    count: number;
    percentage: number;
  }[];
  byProduct: {
    id: string;
    name: string;
    count: number;
    subProducts: {
      id: string;
      name: string;
      count: number;
    }[];
  }[];
  byClassification: {
    id: string;
    name: string;
    color: string;
    count: number;
    percentage: number;
  }[];
}

export interface StatsFilters {
  quarter?: string;
  productId?: string;
  subProductId?: string;
  statusId?: string;
}

export const statsApi = {
  getStats: (filters?: StatsFilters) => {
    const params = new URLSearchParams();
    if (filters?.quarter) params.append('quarter', filters.quarter);
    if (filters?.productId) params.append('productId', filters.productId);
    if (filters?.subProductId) params.append('subProductId', filters.subProductId);
    if (filters?.statusId) params.append('statusId', filters.statusId);
    const queryString = params.toString();
    return apiRequest<StatsResponse>(`/stats${queryString ? `?${queryString}` : ''}`);
  },
  getRecentActivity: (limit = 10, offset = 0) =>
    apiRequest<{ data: any[]; total: number; hasMore: boolean }>(
      `/stats/recent-activity?limit=${limit}&offset=${offset}`
    ),
};

// Auth API
export interface AuthUser {
  id: string;
  email: string;
  name: string;
  title?: string;
  role: 'ADMIN' | 'EDITOR' | 'VIEWER';
  emailNotificationsEnabled?: boolean;
  favoriteTimelineViewId?: string | null;
  favoriteListViewId?: string | null;
  favoriteKanbanViewId?: string | null;
  favoriteCalendarViewId?: string | null;
}

export const authApi = {
  requestOTP: (email: string) =>
    apiRequest<{ success: boolean; message: string }>('/auth/request-otp', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),
  verifyOTP: async (email: string, code: string) => {
    const response = await apiRequest<{ success: boolean; token: string; user: AuthUser }>('/auth/verify-otp', {
      method: 'POST',
      body: JSON.stringify({ email, code }),
    });
    // Store token in localStorage for cross-origin support
    if (response.token) {
      setAuthToken(response.token);
    }
    return response;
  },
  getMe: () =>
    apiRequest<{ user: AuthUser }>('/auth/me'),
  logout: async () => {
    clearAuthToken(); // Clear localStorage token
    clearTenantSlug(); // Clear tenant context
    return apiRequest<{ success: boolean }>('/auth/logout', {
      method: 'POST',
    });
  },
  confirmInvite: (token: string) =>
    apiRequest<{ success: boolean; email: string }>('/auth/confirm-invite', {
      method: 'POST',
      body: JSON.stringify({ token }),
    }),
};

// User API (Admin only)
export interface User {
  id: string;
  email: string;
  name: string;
  title?: string;
  avatarUrl?: string;
  role: 'ADMIN' | 'EDITOR' | 'VIEWER';
  isActive: boolean;
  emailVerified: boolean;
  isPending?: boolean;
  groupId?: string | null;
  teamId?: string | null;
  lastLoginAt?: string;
  createdAt: string;
  updatedAt: string;
}

export const userApi = {
  getAll: () => apiRequest<User[]>('/users', { credentials: 'include' }),
  getById: (id: string) => apiRequest<User>(`/users/${id}`, { credentials: 'include' }),
  create: (data: { email: string; name: string; title?: string; role?: string; groupId?: string | null; teamId?: string | null }) =>
    apiRequest<User>('/users', {
      method: 'POST',
      body: JSON.stringify(data),
      credentials: 'include',
    }),
  update: (id: string, data: { name?: string; title?: string; role?: string; isActive?: boolean; groupId?: string | null; teamId?: string | null }) =>
    apiRequest<User>(`/users/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
      credentials: 'include',
    }),
  delete: (id: string) =>
    apiRequest<void>(`/users/${id}`, {
      method: 'DELETE',
      credentials: 'include',
    }),
  resendInvite: (id: string) =>
    apiRequest<{ success: boolean }>(`/users/${id}/resend-invite`, {
      method: 'POST',
      credentials: 'include',
    }),
};

// Organization API
export interface Organization {
  id: string;
  name: string;
  slug: string;
  status: string;
  plan: string;
  allowedEmailDomains: string[];
  maxUsers: number;
  maxInitiatives: number | null;
  createdAt: string;
  trialExpiresAt: string | null;
}

export const organizationApi = {
  get: () => apiRequest<Organization>('/organization'),
  update: (data: { name?: string; allowedEmailDomains?: string[] }) =>
    apiRequest<Organization>('/organization', {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
};

// Sign-up API (PUBLIC - no tenant slug header needed)
async function signupRequest<T>(endpoint: string, body: Record<string, unknown>): Promise<T> {
  const url = `${API_BASE_URL}/signup${endpoint}`;
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    const msg = data.detail ? `${data.error}: ${data.detail}` : (data.error || `HTTP ${response.status}`);
    throw new Error(msg);
  }
  return response.json();
}

export const signupApi = {
  /** Fire-and-forget: warm up the Vercel function + master DB connection before the user fills the form. */
  preflight: () => {
    const healthUrl = API_BASE_URL.replace('/api/v1', '') + '/health';
    fetch(healthUrl).catch(() => {});
  },
  checkSlug: (slug: string) =>
    signupRequest<{ available: boolean; reason?: string }>('/check-slug', { slug }),
  requestOTP: (data: { orgName: string; slug: string; adminName: string; adminEmail: string }) =>
    signupRequest<{ success: boolean; message: string }>('/request-otp', data),
  verifyOTP: (email: string, code: string, referredBy?: string) =>
    signupRequest<{ success: boolean; token: string; slug: string; user: AuthUser }>('/verify-otp', { email, code, ...(referredBy ? { referredBy } : {}) }),
};

// Intelligence API
export interface AiInitiativeSuggestion {
  title: string;
  titleConfident: boolean;
  classificationId: string | null;
  classificationConfident: boolean;
  statusId: string | null;
  statusConfident: boolean;
  userId: string | null;
  userConfident: boolean;
  startQuarter: string | null;
  startConfident: boolean;
  endQuarter: string | null;
  endConfident: boolean;
  productId: string | null;
  productConfident: boolean;
  subProductId: string | null;
  subProductConfident: boolean;
  contextComment: string;
}

export interface RoadmapHealthResponse {
  analysis: {
    healthScore: number;
    healthLabel: 'Healthy' | 'At Risk' | 'Critical';
    summary: string;
    risks: Array<{ title: string; description: string; severity: 'high' | 'medium' | 'low' }>;
    overloadedOwners: Array<{ name: string; count: number; detail: string }>;
    dependencyRisks: Array<{ title: string; description: string }>;
    balanceInsight: string;
    incomplete: Array<{ title: string; description: string; count: number }>;
    recommendations: string[];
  };
  generatedAt: string;
  cached?: boolean;
}

export interface InitiativeInsightsResponse {
  analysis: {
    pulse: 'on_track' | 'at_risk' | 'blocked' | 'stale' | 'on_hold';
    headline: string;
    summary: string;
    risks: Array<{ description: string; severity: 'high' | 'medium' | 'low'; action: string }>;
    signals: string[];
    considerations: string[];
    userStories: string[];
  };
}

export interface SavedInsight {
  id: string;
  createdAt: string;
  generatedBy: string;
  analysis: {
    pulse: 'on_track' | 'at_risk' | 'blocked' | 'stale' | 'on_hold';
    headline: string;
    summary: string;
    risks: Array<{ description: string; severity: 'high' | 'medium' | 'low'; action: string }>;
    signals: string[];
    considerations: string[];
    userStories: string[];
  };
}

// Helper to read a streaming SSE response from fetch
export async function* readStream(response: Response): AsyncGenerator<string> {
  const reader = response.body?.getReader();
  if (!reader) return;

  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() || '';

    for (const line of lines) {
      if (line.startsWith('data: ')) {
        const data = line.slice(6).trim();
        if (data === '[DONE]') return;
        try {
          const parsed = JSON.parse(data);
          if (parsed.text) yield parsed.text;
        } catch {
          // ignore parse errors
        }
      }
    }
  }
}

// Perform a streaming request, returns the raw Response (caller uses readStream)
async function streamingRequest(endpoint: string, body: Record<string, unknown>): Promise<Response> {
  const url = `${API_BASE_URL}${endpoint}`;
  const token = getAuthToken();

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-tenant-slug': getTenantSlug(),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    credentials: 'include',
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const text = await response.text();
    throw new ApiError(
      `HTTP ${response.status}`,
      'POST',
      url,
      response.status,
      response.statusText,
      text
    );
  }

  return response;
}

// AI Provider Config API
export interface AiProviderConfig {
  id: string;
  mode: 'builtin' | 'custom';
  provider?: string | null;
  apiKey?: string | null; // masked on read: "••••xxxx"
  model?: string | null;
  updatedAt: string;
}

export function getAiProviderLabel(config: AiProviderConfig | null): string {
  if (!config || config.mode === 'builtin') return 'Claude';
  switch (config.provider) {
    case 'anthropic': return 'Claude';
    case 'openai': return 'OpenAI';
    case 'gemini': return 'Gemini';
    case 'openrouter': return 'OpenRouter';
    default: return 'AI';
  }
}

export function getAiProviderModel(config: AiProviderConfig | null): string {
  if (!config || config.mode === 'builtin') return 'claude-haiku-4-5-20251001';
  return config.model || '';
}

export const aiProviderApi = {
  get: () => apiRequest<AiProviderConfig>('/ai-provider'),
  update: (data: { mode: string; provider?: string; model?: string; apiKey?: string }) =>
    apiRequest<AiProviderConfig>('/ai-provider', {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
};

export interface AiCreditTransaction {
  id: string;
  amountCents: number;
  credits: number;
  createdAt: string;
}

export interface AiUsage {
  createAiUsed: number;
  createAiLimit: number;
  insightAiUsed: number;
  insightAiLimit: number;
  healthAiUsed: number;
  healthAiLimit: number;
  askAiUsed: number;
  askAiLimit: number;
  digestAiUsed: number;
  digestAiLimit: number;
  paidAiCredits: number;
  isCustomMode: boolean;
  transactions: AiCreditTransaction[];
}

export const billingApi = {
  getUsage: () => apiRequest<AiUsage>('/billing/usage'),
  createCheckout: (amountCents: number, returnUrl: string) =>
    apiRequest<{ url: string }>('/billing/checkout', {
      method: 'POST',
      body: JSON.stringify({ amountCents, returnUrl }),
    }),
  resetUsage: () => apiRequest<{ ok: boolean }>('/billing/reset-usage', { method: 'POST' }),
};

// Jira API
export interface JiraConfig {
  instanceUrl: string | null;
  email: string | null;
  apiToken: string | null; // masked on read
  updatedAt: string | null;
}

export interface JiraConfigInput {
  instanceUrl: string;
  email: string;
  apiToken?: string;
}

export interface JiraProject {
  key: string;
  name: string;
}

export interface JiraIssueType {
  id: string;
  name: string;
}

export interface JiraPushInput {
  projectKey: string;
  issueTypeId: string;
  summary?: string;
}

export const jiraApi = {
  getConfig: () => apiRequest<JiraConfig>('/jira/config'),
  saveConfig: (data: JiraConfigInput) =>
    apiRequest<JiraConfig>('/jira/config', { method: 'PUT', body: JSON.stringify(data) }),
  testConnection: () =>
    apiRequest<{ ok: boolean; displayName?: string; error?: string }>('/jira/test', { method: 'POST' }),
  getProjects: () => apiRequest<JiraProject[]>('/jira/projects'),
  getIssueTypes: (projectKey: string) =>
    apiRequest<JiraIssueType[]>(`/jira/projects/${projectKey}/issue-types`),
  pushInitiative: (initiativeId: string, data: JiraPushInput) =>
    apiRequest<{ jiraIssueKey: string; jiraIssueUrl: string }>(
      `/jira/initiatives/${initiativeId}/push`,
      { method: 'POST', body: JSON.stringify(data) }
    ),
};

export const intelligenceApi = {
  createInitiative: (description: string) =>
    apiRequest<{ suggestion: AiInitiativeSuggestion }>('/intelligence/create-initiative', {
      method: 'POST',
      body: JSON.stringify({ description }),
    }),
  getRoadmapHealth: () =>
    apiRequest<RoadmapHealthResponse>('/intelligence/roadmap-health'),
  getInsightHistory: (id: string) =>
    apiRequest<{ insights: SavedInsight[] }>(`/intelligence/initiative/${id}/history`),
  generateInsight: (id: string) =>
    apiRequest<SavedInsight>(`/intelligence/initiative/${id}`, { method: 'POST' }),
  ask: (question: string) =>
    streamingRequest('/intelligence/ask', { question }),
  generateDigest: (format: 'slack' | 'executive' | 'team', daysBack: number = 7) =>
    streamingRequest('/intelligence/digest', { format, daysBack }),
};

// Workspace / Login API (PUBLIC - no tenant slug header needed)
async function publicRequest<T>(endpoint: string, body: Record<string, unknown>): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`;
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    const msg = data.detail ? `${data.error}: ${data.detail}` : (data.error || `HTTP ${response.status}`);
    throw new Error(msg);
  }
  return response.json();
}

export interface WorkspaceInfo {
  slug: string;
  name: string;
}

export interface LoginOTPResponse {
  success: boolean;
  message: string;
}

export interface VerifyLoginOTPResponse {
  // Single workspace (auto-login)
  token?: string;
  user?: AuthUser;
  slug?: string;
  workspaceName?: string;
  // Multiple workspaces (picker)
  workspaces?: WorkspaceInfo[];
  sessionToken?: string;
}

export interface SelectWorkspaceResponse {
  token: string;
  user: AuthUser;
  slug: string;
  workspaceName: string;
}

export interface CheckWorkspaceResponse {
  valid: boolean;
  name?: string;
}

export const workspaceApi = {
  sendLoginOTP: (email: string, slug?: string) =>
    publicRequest<LoginOTPResponse>('/auth/login-otp', { email, ...(slug ? { slug } : {}) }),
  verifyLoginOTP: (email: string, code: string) =>
    publicRequest<VerifyLoginOTPResponse>('/auth/verify-login-otp', { email, code }),
  selectWorkspace: (sessionToken: string, slug: string) =>
    publicRequest<SelectWorkspaceResponse>('/auth/select-workspace', { sessionToken, slug }),
  checkWorkspace: (slug: string) =>
    publicRequest<CheckWorkspaceResponse>('/auth/check-workspace', { slug }),
};

// ─── API Keys ────────────────────────────────────────────────────────────────

export interface ApiKey {
  id: string;
  name: string;
  keyPrefix: string;
  createdAt: string;
  lastUsedAt: string | null;
  creator: { id: string; name: string; email: string };
}

export interface CreateApiKeyResponse {
  apiKey: ApiKey;
  key: string; // full key — shown once only, never stored
}

export const apiKeyApi = {
  getAll: () =>
    apiRequest<{ keys: ApiKey[] }>('/api-keys', { credentials: 'include' }),

  create: (data: { name: string }) =>
    apiRequest<CreateApiKeyResponse>('/api-keys', {
      method: 'POST',
      body: JSON.stringify(data),
      credentials: 'include',
    }),

  revoke: (id: string) =>
    apiRequest<void>(`/api-keys/${id}`, {
      method: 'DELETE',
      credentials: 'include',
    }),
};

// Groups API
export interface Group {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  isActive: boolean;
  displayOrder: number;
  teams?: { id: string; name: string }[];
}

export const groupApi = {
  getAll: () => apiRequest<Group[]>('/groups'),
  create: (data: { name: string; description?: string; displayOrder?: number }) =>
    apiRequest<Group>('/groups', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: { name?: string; description?: string; displayOrder?: number; isActive?: boolean }) =>
    apiRequest<Group>(`/groups/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id: string) => apiRequest<void>(`/groups/${id}`, { method: 'DELETE' }),
};

// Teams API
export interface Team {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  color: string | null;
  icon: string | null;
  groupId: string | null;
  group: { id: string; name: string } | null;
  isActive: boolean;
}

export const teamApi = {
  getAll: () => apiRequest<Team[]>('/teams'),
  create: (data: { name: string; description?: string; groupId?: string }) =>
    apiRequest<Team>('/teams', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: { name?: string; description?: string; groupId?: string | null; isActive?: boolean }) =>
    apiRequest<Team>(`/teams/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id: string) => apiRequest<void>(`/teams/${id}`, { method: 'DELETE' }),
};

export const profileApi = {
  update: (data: { name: string; title?: string }) =>
    apiRequest<{ id: string; name: string; title: string | null; email: string; role: string }>(
      '/auth/me',
      { method: 'PUT', body: JSON.stringify(data) }
    ),
};
